import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Mic } from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { CreateEpisodeModal } from '@/components/episodes/CreateEpisodeModal'
import { useLabels } from '@/lib/terminology'
import { singular } from '@/lib/terminology'

interface EpisodeData {
  id: string
  number: number
  title: string
  recording_date: string
  status: string
}

export function RecordingsPage() {
  const { membership } = useAuth()
  const supabase = createClient()
  const navigate = useNavigate()
  const labels = useLabels()
  const [showCreateEpisode, setShowCreateEpisode] = useState(false)

  const { data: episodes = [], isLoading } = useQuery({
    queryKey: ['recordings', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return []

      const { data } = await supabase
        .from('episodes')
        .select('*')
        .eq('team_id', membership.team_id)
        .order('recording_date', { ascending: false })

      return (data as EpisodeData[]) || []
    },
    enabled: !!membership?.team_id,
  })

  // Check if team has any series
  const { data: seriesCount = 0 } = useQuery({
    queryKey: ['series-count', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return 0

      const { count, error } = await supabase
        .from('series')
        .select('*', { count: 'exact', head: true })
        .eq('team_id', membership.team_id)

      if (error) throw error
      return count || 0
    },
    enabled: !!membership?.team_id,
  })

  const hasCollections = seriesCount > 0

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
          <div className="h-4 w-64 bg-zinc-200 dark:bg-zinc-800 rounded mb-6" />
          <div className="bg-zinc-100 dark:bg-zinc-800/50 rounded-xl h-64" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{labels.recordings}</h1>
        </div>
        {hasCollections && (
          <button
            onClick={() => setShowCreateEpisode(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Episode
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800">
        {episodes.length === 0 ? (
          <div className="p-12 text-center">
            <Mic className="w-12 h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-900 dark:text-zinc-100 font-medium mb-2">No {labels.recordings.toLowerCase()} yet</p>
            {hasCollections ? (
              <>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                  Create an episode to schedule your first recording
                </p>
                <button
                  onClick={() => setShowCreateEpisode(true)}
                  className="px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-500 hover:border-primary-600 rounded-lg transition-colors"
                >
                  Create First Episode
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                  Create a {singular(labels.collections).toLowerCase()} first, then add {labels.recordings.toLowerCase()}
                </p>
                <Link
                  to="/collections"
                  className="inline-block px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-500 hover:border-primary-600 rounded-lg transition-colors"
                >
                  Go to {labels.collections}
                </Link>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {episodes.map((episode) => (
              <button
                key={episode.id}
                onClick={() => navigate(`/recordings/${episode.id}`)}
                className="w-full text-left p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">
                      Episode {episode.number}: {episode.title}
                    </p>
                    <p className="text-sm text-zinc-500 mt-1">
                      Recording: {new Date(episode.recording_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      episode.status === 'published'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : episode.status === 'recorded'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-400'
                    }`}
                  >
                    {episode.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create episode modal */}
      <CreateEpisodeModal
        isOpen={showCreateEpisode}
        onClose={() => setShowCreateEpisode(false)}
      />
    </div>
  )
}
